import type { OrganizationRiskSetting } from '@app/src/types/resourceExplorer'
import { RiskIndexType, SustainabilityArea } from '@app/src/types/resourceExplorer'
import type { SustainabilityTopic } from '@app/src/types/topicalRisk'

export const DEFAULT_COUNTRY_WEIGHT = 60
export const DEFAULT_INDUSTRY_WEIGHT = 40

export interface TopicCard {
  key: string
  topicId: number
  title: string
  area: SustainabilityArea
  countrySetting?: OrganizationRiskSetting
  industrySetting?: OrganizationRiskSetting
  isActive: boolean
}

export interface RiskIndexPartition {
  /** Indices linked to a sustainability topic, grouped one card per topic. */
  topicCards: TopicCard[]
  /** Indices without a resolvable sustainability topic — they never feed topic-level inherent risk. */
  standaloneSettings: OrganizationRiskSetting[]
}

export type WeightingFooter =
  | { kind: 'both'; countryWeight: number; industryWeight: number }
  | { kind: 'countryOnly' }
  | { kind: 'industryOnly' }
  | { kind: 'inactive' }

export interface RiskSettingChange {
  riskIndexId: number
  isActivated: boolean
}

const countryOf = (settings: OrganizationRiskSetting[]) =>
  settings.find(s => s.riskType.riskIndexType === RiskIndexType.Country)

const industryOf = (settings: OrganizationRiskSetting[]) =>
  settings.find(s => s.riskType.riskIndexType === RiskIndexType.Industry)

const toCard = (topic: SustainabilityTopic, settings: OrganizationRiskSetting[]): TopicCard => ({
  key: `topic-${topic.id}`,
  topicId: topic.id,
  title: topic.name,
  area: topic.sustainabilityArea,
  countrySetting: countryOf(settings),
  industrySetting: industryOf(settings),
  isActive: settings.some(s => s.isActivated),
})

/**
 * Splits the risk settings into the topic-grouped cards and the standalone indices. An index whose
 * sustainabilityTopicId doesn't resolve to a fetched topic counts as standalone — some topics are
 * created outside of migrations and are missing in certain databases, and the standalone list still
 * renders such an index correctly by its source name.
 */
export const partitionRiskSettings = (
  settings: OrganizationRiskSetting[],
  topics: SustainabilityTopic[],
): RiskIndexPartition => {
  const topicsById = new Map(topics.map(topic => [topic.id, topic]))

  const settingsByTopicId = new Map<number, OrganizationRiskSetting[]>()
  const standaloneSettings: OrganizationRiskSetting[] = []

  settings.forEach(setting => {
    const topicId = setting.riskType.sustainabilityTopicId
    if (topicId !== undefined && topicsById.has(topicId)) {
      const group = settingsByTopicId.get(topicId) ?? []
      group.push(setting)
      settingsByTopicId.set(topicId, group)
    } else {
      standaloneSettings.push(setting)
    }
  })

  const topicCards = topics
    .filter(topic => settingsByTopicId.has(topic.id))
    .map(topic => toCard(topic, settingsByTopicId.get(topic.id) ?? []))

  return { topicCards, standaloneSettings }
}

const AREA_ORDER: SustainabilityArea[] = [
  SustainabilityArea.Environment,
  SustainabilityArea.Social,
  SustainabilityArea.Governance,
  SustainabilityArea.SupplyChain,
]

/** Grouped by ESG area (Environment → Social → Governance), then alphabetically by title within each area. */
export const sortTopicCards = (cards: TopicCard[]): TopicCard[] =>
  [...cards].sort((a, b) => {
    const areaDiff = AREA_ORDER.indexOf(a.area) - AREA_ORDER.indexOf(b.area)
    return areaDiff !== 0 ? areaDiff : a.title.localeCompare(b.title)
  })

export const weightingFooter = (card: TopicCard): WeightingFooter => {
  const countryActive = card.countrySetting?.isActivated ?? false
  const industryActive = card.industrySetting?.isActivated ?? false

  if (countryActive && industryActive)
    return {
      kind: 'both',
      countryWeight: card.countrySetting?.weight ?? DEFAULT_COUNTRY_WEIGHT,
      industryWeight: card.industrySetting?.weight ?? DEFAULT_INDUSTRY_WEIGHT,
    }

  if (countryActive) return { kind: 'countryOnly' }
  if (industryActive) return { kind: 'industryOnly' }
  return { kind: 'inactive' }
}

export const applyTopicToggle = (card: TopicCard, isActivated: boolean): RiskSettingChange[] =>
  [card.countrySetting, card.industrySetting]
    .filter((setting): setting is OrganizationRiskSetting => setting !== undefined)
    .map(setting => ({ riskIndexId: setting.riskType.id, isActivated }))

/**
 * Overlays pending edits (riskIndexId → desired isActivated) onto the fetched settings so the
 * cards reflect unsaved changes. Settings without a pending edit are returned unchanged.
 */
export const applyModifications = (
  settings: OrganizationRiskSetting[],
  modified: Map<number, boolean>,
): OrganizationRiskSetting[] =>
  settings.map(setting => {
    const modifiedState = modified.get(setting.riskType.id)
    return modifiedState === undefined ? setting : { ...setting, isActivated: modifiedState }
  })
